import { debounce } from 'debounce'
import flatpickr from 'flatpickr'
import { getNextSiblingWithClass, waitForElement } from '../../js/utils'
import FormCheck from '../forms/form-check/form-check'
import FormLabel from '../forms/form-label/form-label'
import FormControl from '../forms/form-control/form-control'
import FormSelect from '../forms/form-select/form-select'
import { Modal, Popover } from 'bootstrap'

let waiverShown = false

const hideForm = () => {
  document.documentElement.classList.add('p2p-registration-hidden')
}

const showForm = () => {
  document.documentElement.classList.remove('p2p-registration-hidden')
}

class RegistrationTypes {
  constructor() {
    this.registrationTypes = null

    waitForElement('.en__p2p__registration__types__list__item').then(() => {
      this.registrationTypes = document.querySelector('.en__p2p__registration__types')
      this.render()
    })
  }

  render() {
    // makes the registration type list accessible by keyboard
    const registrationTypesList = document.querySelector('.en__p2p__registration__types__list')

    if (registrationTypesList) {
      registrationTypesList.setAttribute('role', 'radiogroup')
      registrationTypesList.setAttribute('aria-label', 'Registration Type')

      const registrationTypeLabels = document.querySelectorAll('.en__p2p__registration__type')
      // make the registration type labels clickable and focusable. set aria attributes to indicate the selected registration type
      registrationTypeLabels.forEach((registrationTypeLabel) => {
        registrationTypeLabel.setAttribute('role', 'radio')
        registrationTypeLabel.setAttribute('aria-checked', 'false')
        registrationTypeLabel.setAttribute('tabindex', '0')
        registrationTypeLabel.addEventListener('click', () => {
          registrationTypeLabels.forEach((registrationTypeLabel) => {
            registrationTypeLabel.setAttribute('aria-checked', 'false')
          })
          registrationTypeLabel.setAttribute('aria-checked', 'true')
        })

        registrationTypeLabel.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.target.querySelector('.en__p2p__registration__type__radio').click()
            registrationTypeLabels.forEach((registrationTypeLabel) => {
              registrationTypeLabel.setAttribute('aria-checked', 'false')
            })
            registrationTypeLabel.setAttribute('aria-checked', 'true')
          }
        })
      })
    }
  }
}

class RegistrationForm {
  constructor() {
    this.form = null
    waitForElement('.en__p2p__registration__form input').then((registrationForm) => {
      document.querySelectorAll('.en__p2p__registration__form').forEach((form) => {
        this.renderFields(form)
      })
    })
  }

  renderFields(form) {
    waitForElement('.en__formField__label').then(() => {
      form.querySelectorAll('.en__formField__label').forEach((label) => {
        new FormLabel(label)
      })
    })

    waitForElement('.en__input input').then(() => {
      form.querySelectorAll('.en__input input').forEach((input) => {
        new FormControl(input)
      })
    })

    waitForElement('.en__input--email input').then(() => {
      form.querySelectorAll('.en__input--email input').forEach((input) => {
        new FormControl(input)
      })
    })

    waitForElement('.en__formField--select select').then(() => {
      form.querySelectorAll('.en__formField--select select').forEach((select) => {
        new FormSelect(select)
      })
    })
  }
}

class registrationWaiverModal {
  constructor() {
    waitForElement('#waiverModal').then((waiverModalElement) => {
      this.waiverModalElement = waiverModalElement
      this.pageContent = document.querySelector('.content')
      this.render()
    })
  }

  render() {
    if (waiverShown) return

    waiverShown = true
    this.pageContent.setAttribute('inert', 'true')
    this.pageContent.classList.add('position-fixed')
    document.body.prepend(this.waiverModalElement)
    this.waiverModal = new Modal(this.waiverModalElement, {
      keyboard: false,
    })
    this.waiverModalElement.addEventListener('hidden.bs.modal', () => {
      this.pageContent.removeAttribute('inert')
      this.pageContent.classList.remove('position-fixed')
    })
    this.waiverModal.show()
  }
}

class RegistrationTeamCreate {
  constructor() {
    this.teamCreate = null
    waitForElement('.en__p2p__registration__team__create__start__button').then((button) => {
      this.teamCreateButton = button
      this.handleTeamCreateButtonClick()
    })
  }

  handleTeamCreateButtonClick() {
    this.teamCreateButton.addEventListener('click', () => {
      waitForElement('.en__p2p__registration__team__create__form input').then(() => {
        this.render()
      })
    })
  }

  render() {
    this.renderFields()
  }

  renderFields() {
    document.querySelectorAll('.en__p2p__registration__team__create__form label').forEach((input) => {
      input.classList.add('form-label')
    })

    document.querySelectorAll('.en__p2p__registration__team__create__form input').forEach((input) => {
      input.classList.add('form-control')
    })

    document.querySelector('.en__p2p__registration__team__create__form__goal .en__formField__field')?.classList.add('input-group')
    document.querySelector('.en__p2p__registration__team__create__form__goal .en__input__prefix')?.classList.add('input-group-text')
  }
}

class RegistrationTeamSelect {
  constructor() {
    this.teamSelect = null
    waitForElement('.en__p2p__registration__team__select__options__item').then(() => {
      this.teamSelectOptions = document.querySelector('.en__p2p__registration__team__select__options')
      this.render()
    })
  }

  render() {
    this.makeTeamSelectAccessible()
    this.handleOptionClick()
    this.handleResize()
    this.renderFields()
  }

  makeTeamSelectAccessible() {
    const teamSelectOptions = this.teamSelectOptions

    teamSelectOptions.setAttribute('role', 'radiogroup')
    teamSelectOptions.setAttribute('aria-label', 'Team Options')
    const teamSelectOptionsLabels = document.querySelectorAll('.en__p2p__registration__team__select__option')
    // make the registration type labels clickable and focusable. set aria attributes to indicate the selected registration type
    teamSelectOptionsLabels.forEach((teamSelectOptionLabel) => {
      teamSelectOptionLabel.setAttribute('role', 'radio')
      teamSelectOptionLabel.setAttribute('aria-checked', 'false')
      teamSelectOptionLabel.setAttribute('tabindex', '0')
      teamSelectOptionLabel.addEventListener('click', () => {
        teamSelectOptionsLabels.forEach((teamSelectOptionLabel) => {
          teamSelectOptionLabel.setAttribute('aria-checked', 'false')
        })
        teamSelectOptionLabel.setAttribute('aria-checked', 'true')
      })

      teamSelectOptionLabel.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.target.querySelector('.en__p2p__registration__team__select__option__radio').click()
          teamSelectOptionsLabels.forEach((teamSelectOptionLabel) => {
            teamSelectOptionLabel.setAttribute('aria-checked', 'false')
          })
          teamSelectOptionLabel.setAttribute('aria-checked', 'true')
        }
      })
    })
  }

  handleOptionClick() {
    const teamSelectOptionsLabels = document.querySelectorAll('.en__p2p__registration__team__select__option')

    teamSelectOptionsLabels.forEach((teamSelectOptionLabel) => {
      teamSelectOptionLabel.addEventListener('click', (e) => {
        waitForElement('.en__p2p__registration__team__detail').then((teamDetail) => {
          this.renderFields(teamDetail)
        })
      })
    })
  }

  handleResize() {
    window.addEventListener('resize', () => {
      waitForElement('.en__p2p__registration__team__detail').then((teamDetail) => {
        this.renderFields(teamDetail)
      })
    })
  }

  renderFields(teamDetail) {
    if (!teamDetail) return
    teamDetail.querySelector('input')?.classList.add('form-control', 'form-control-sm')
    teamDetail.querySelector('button')?.classList.add('btn', 'btn-sm', 'btn-primary')

    waitForElement('.en_p2p_mobile_team_tab').then((mobileTeamTab) => {
      mobileTeamTab.classList.add('.form-select', 'form-select-sm')
    })
  }
}

class RegistrationGoal {
  constructor() {
    this.goal = null
    waitForElement('.en__p2p__registration__goal .en__formField__field').then(() => {
      this.registrationGoal = document.querySelector('.en__p2p__registration__goal')
      this.render(this.registrationGoal)
    })
  }

  render(registrationGoal) {
    this.renderFields(registrationGoal)
  }

  renderFields(registrationGoal) {
    registrationGoal.closest('.en__column')?.classList.add('en__p2p__registration__goal--container')
    registrationGoal.previousElementSibling?.classList.add('text-center')
    registrationGoal.querySelector('.en__formField__label').classList.add('form-label')
    registrationGoal.querySelector('.en__formField__field').classList.add('input-group')
    registrationGoal.querySelector('.en__input__prefix').classList.add('input-group-text')
    registrationGoal.querySelector('input').classList.add('form-control')
  }
}

class RegistrationStartingDonation {
  constructor() {
    waitForElement('.en__p2p__registration__donation__donationAmounts input').then(() => {
      this.registrationDonation = document.querySelector('.en__p2p__registration__donation')
      this.render(this.registrationDonation)
    })
  }

  render(registrationDonation) {
    this.renderButtons(registrationDonation)
  }

  renderButtons(registrationDonation) {
    registrationDonation.querySelector('.en__p2p__registration__donation__noDonation button').classList.add('btn', 'btn-link')
  }
}

class smsOptIn {
  constructor() {
    waitForElement('.en__formField__label', 'text messages').then((smsOptInLabel) => {
      this.smsOptIn = smsOptInLabel[0].closest('.en__formField')
      this.form = this.smsOptIn?.closest('.en__p2p__registration__form')

      waitForElement('[data-en-formfieldtarget="phoneNumber2"]').then((mobilePhoneField) => {
        this.mobilePhoneField = mobilePhoneField

        setTimeout(() => {
          this.disclaimer = this.form && getNextSiblingWithClass(this.form, 'en__p2p__text')
          this.yesRadio = this.smsOptIn.querySelector('.en__radio__input[value="Y"]')
          this.noRadio = this.smsOptIn.querySelector('.en__radio__input[value="N"]')
          this.render()
          this.handleOptInChange()
        }, 600)
      })
    })
  }

  render() {
    this.form.classList.add('mt-3')
    this.mobilePhoneField.classList.add('my-2')
    if (this.yesRadio.checked) {
      this.smsOptIn.classList.add('sms-yes-selected')
    }
    this.disclaimer.classList.add('alert', 'alert-light')
    this.smsOptIn.querySelectorAll('.en__radio').forEach((radioContainer) => {
      radioContainer.classList.add('form-check')
    })
    this.smsOptIn.querySelectorAll('.en__radio__input').forEach((radio) => radio.classList.add('form-check-input'))
    this.smsOptIn.querySelectorAll('.en__radio__label').forEach((label) => label.classList.add('form-check-label'))
  }

  handleOptInChange() {
    const mobilePhoneInput = this.mobilePhoneField.querySelector('input')
    this.smsOptIn.querySelector('.en__radio__input[value="Y"]').addEventListener('change', () => {
      this.render()
      this.smsOptIn.classList.add('sms-yes-selected')
      mobilePhoneInput.value = ''
      mobilePhoneInput.removeAttribute('disabled')
    })
    this.smsOptIn.querySelector('.en__radio__input[value="N"]').addEventListener('change', () => {
      this.render()
      this.smsOptIn.classList.remove('sms-yes-selected')
      mobilePhoneInput.value = ' '
      mobilePhoneInput.dispatchEvent(new Event('input', { bubbles: true }))
      mobilePhoneInput.setAttribute('disabled', 'true')
    })
  }
}

class UniversalOptIn {
  constructor(optIn) {
    this.optIn = optIn
    this.form = this.optIn.closest('.en__p2p__registration__form')
    this.yesRadio = this.optIn.querySelector('.en__radio__input[value="Y"]')
    this.noRadio = this.optIn.querySelector('.en__radio__input[value="N"]')

    this.render()
    this.makePopover()
    this.handleOptInChange()
  }

  render() {
    this.optIn.classList.add('universal-opt-in')

    new FormCheck(this.yesRadio.closest('.en__radio'))
    new FormCheck(this.noRadio.closest('.en__radio'))
  }

  makePopover() {
    this.noRadio.setAttribute('data-bs-toggle', 'popover')
    this.popover = new Popover(this.noRadio, {
      customClass: 'popover-danger',
      trigger: 'manual',
      placement: 'bottom',
      content: 'Are you sure? You will miss out on notifications from this event.',
    })

    this.noRadio.addEventListener('click', this.showPopover.bind(this), { once: true })
  }

  showPopover() {
    this.popover.show()

    setTimeout(() => {
      document.body.addEventListener('click', this.hidePopover.bind(this), { once: true })
    }, 300)

    setTimeout(() => {
      this.hidePopover()
    }, 3000)
  }

  hidePopover() {
    this.popover.hide()
  }

  handleOptInChange() {
    const hiddenOptIns = Array.from(this.form.querySelectorAll('[data-en-formfieldtargettype="optin"]')).filter((optIn) => optIn !== this.optIn)

    this.yesRadio.addEventListener('change', () => {
      hiddenOptIns.forEach((optIn) => {
        optIn.querySelector('.en__radio__input[value="N"]').checked = false
        optIn.querySelector('.en__radio__input[value="Y"]').checked = true
      })
      this.render()
    })

    this.noRadio.addEventListener('change', () => {
      hiddenOptIns.forEach((optIn) => {
        optIn.querySelector('.en__radio__input[value="Y"]').checked = false
        optIn.querySelector('.en__radio__input[value="N"]').checked = true
      })
      this.render()
    })
  }
}

class FundraiserSettings {
  constructor() {
    this.fundraiserSettings = null
    waitForElement('.en__p2p__registration__fundraiserSettings .en__checkbox').then((fundraiserSettings) => {
      this.fundraiserSettings = document.querySelector('.en__p2p__registration__fundraiserSettings')
      this.render()
    })
  }

  render() {
    this.fundraiserSettings.querySelectorAll('.en__formField__label').forEach((label) => {
      label.classList.add('form-label')
    })

    this.fundraiserSettings.querySelectorAll('.en__checkbox').forEach((checkbox) => {
      new FormCheck(checkbox)
      checkbox.addEventListener('change', () => {
        this.render()
      })
    })
  }
}

class NextButton {
  constructor() {
    this.button = null
    waitForElement('.en__p2p__button', 'Next').then((nextButton) => {
      // console.log('next button found')
      Array.isArray(nextButton) ? (nextButton = nextButton[0]) : (nextButton = nextButton)

      this.button = nextButton
      this.render()
    })
  }

  render() {
    this.button.classList.add('btn', 'btn-lg', 'btn-primary')
    this.button.addEventListener('click', debounce(this.handleClick, 500, true))
  }

  handleClick() {
    new Registration()
  }
}
class Registration {
  constructor() {
    waitForElement('.en__p2p__registration').then((registration) => {
      this.registration = registration
      this.render()
      this.handlePopState()
    })
  }

  render() {
    document.documentElement.classList.add('p2p-registration')
    this.registration?.classList.add('container')
    new RegistrationTypes()
    new RegistrationForm()
    new smsOptIn()
    new registrationWaiverModal()
    new RegistrationTeamCreate()
    new RegistrationTeamSelect()
    new RegistrationGoal()
    new RegistrationStartingDonation()
    new FundraiserSettings()
    new NextButton()
  }

  handlePopState() {
    // re-renders the registration form when the browser history changes
    // window.addEventListener('popstate', this.render)
  }
}

export const registration = () => {
  getRegistrationForm()
}

const getRegistrationForm = () => {
  waitForElement('.en__p2p__registration').then((registrationForm) => {
    handleEventDate()
    handleOptIns()
    showForm()
  })
}

const handleEventDate = () => {
  // attaches flatpickr to the event date input
  const yearDropdownPlugin = function () {
    // adds flatpickr plugin to add a year dropdown to the event date input
    return function () {
      return {
        onReady: function onReady() {
          const yearInput = document.querySelector('.numInput')
          const yearList = document.createElement('datalist')

          if (yearInput) {
            yearInput.setAttribute('list', 'years')
            yearInput.setAttribute('placeholder', 'Year')
            yearList.setAttribute('id', 'years')

            for (let i = new Date().getFullYear() - 1; i >= 1900; i--) {
              const option = document.createElement('option')
              option.setAttribute('value', i.toString())
              yearList.appendChild(option)
            }
            yearInput.insertAdjacentHTML('afterend', yearList.outerHTML)
          }
        },
      }
    }
  }

  setTimeout(() => {
    const eventDateInput = document.querySelector('[data-en-formfieldtargettype="siteField"] input[type="text"]')

    if (eventDateInput) {
      eventDateInput.setAttribute('inputmode', 'numeric')
      eventDateInput.closest('.en__formField')?.classList.add('has-flatpickr')

      flatpickr(eventDateInput, {
        plugins: [yearDropdownPlugin()],
        dateFormat: 'Y/m/d',
        altInput: true,
        altInputClass: 'flatpicker-input', // to avoid the altInput being styled as a form-control
        altFormat: 'm/d/Y',
        allowInput: true,
        minDate: 'today',
        disableMobile: 'true',
      })
      eventDateInput.classList.remove('form-control')
    }
  }, 1000)
}

const handleOptIns = () => {
  // universal opt in does not exist yet so set an interval to check for it
  const interval = setInterval(() => {
    const universalOptIn = document.querySelector('[data-en-formfieldtargettype="optin"]')
    if (universalOptIn && universalOptIn.querySelector('.en__radio__input[value="Y"]') && universalOptIn.querySelector('.en__radio__input[value="N"]')) {
      new UniversalOptIn(universalOptIn)
    }
  }, 100)

  setTimeout(() => {
    clearInterval(interval)
  }, 5000)
}

export default Registration
